import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";

const STATUS_META = {
  present:  { label: "P", color: "bg-emerald-500/20 border-emerald-500 text-emerald-300", full: "Present" },
  late:     { label: "L", color: "bg-amber/20 border-amber text-amber",                   full: "Late" },
  excused:  { label: "E", color: "bg-sky-500/20 border-sky-400 text-sky-300",              full: "Excused" },
  absent:   { label: "A", color: "bg-red-500/20 border-red-500 text-red-300",             full: "Absent" },
};

const EMPTY = [];

function useRosterGroups(attendance) {
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster");
        setRoster(data);
      } catch (e) {
        console.error("Failed to load roster for attendance", e);
        toast.error("Failed to load roster. Please try again.");
      }
    })();
  }, []);

  const players = roster?.players || EMPTY;
  const grouped = useMemo(() => {
    const g = {};
    for (const p of players) (g[p.position || "—"] = g[p.position || "—"] || []).push(p);
    return g;
  }, [players]);

  const orderedPositions = useMemo(() => {
    const order = [...(roster?.positions?.standard || []), ...(roster?.positions?.custom || [])];
    const rank = (pos) => { const i = order.indexOf(pos); return i === -1 ? 999 : i; };
    return Object.keys(grouped).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [grouped, roster]);

  const counts = useMemo(() => {
    const c = { present: 0, late: 0, excused: 0, absent: 0, unmarked: 0 };
    for (const p of players) {
      const s = attendance?.[p.id]?.status;
      if (s && c[s] !== undefined) c[s] += 1;
      else c.unmarked += 1;
    }
    return c;
  }, [players, attendance]);

  return { roster, players, grouped, orderedPositions, counts };
}

function AttendanceSummary({ saved, roster, counts, players }) {
  return (
          <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-0.5">
            {!saved && "Save the practice to take attendance"}
            {saved && !roster && "Loading roster…"}
            {saved && roster && roster.players.length === 0 && "No roster synced yet — sync from your Google Sheet"}
            {saved && roster && roster.players.length > 0 && (
              <span>
                <span className="text-emerald-400">{counts.present} present</span>
                {" · "}<span className="text-amber">{counts.late} late</span>
                {" · "}<span className="text-sky-300">{counts.excused} excused</span>
                {" · "}<span className="text-red-400">{counts.absent} absent</span>
                {" · "}<span className="text-slate-500">{counts.unmarked} unmarked</span>
                {"  ·  "}<span className="text-slate-400">{players.length} on roster</span>
              </span>
            )}
          </div>
  );
}

function AttendanceRow({ pl, attendance, setStatus }) {
  const cur = attendance?.[pl.id]?.status;
  const nameTone = pl.ad_cleared ? "text-emerald-400" : "text-red-400";
  return (
                        <div
                          key={pl.id}
                          className="flex items-center gap-2 border border-surface-3 bg-background px-3 py-2"
                          data-testid={`att-row-${pl.id}`}
                        >
                          <div className={`flex-1 text-sm font-semibold ${nameTone}`}>
                            {pl.last_name}, {pl.first_name}
                          </div>
                          <div className="flex gap-1">
                            {Object.entries(STATUS_META).map(([code, meta]) => {
                              const on = cur === code;
                              return (
                                <button
                                  key={code}
                                  onClick={() => setStatus(pl.id, cur, code)}
                                  title={meta.full}
                                  data-testid={`att-${code}-${pl.id}`}
                                  className={`w-7 h-7 border text-xs font-bold flex items-center justify-center transition-colors ${
                                    on
                                      ? meta.color
                                      : "border-surface-3 text-slate-500 hover:border-slate-400 hover:text-slate-300"
                                  }`}
                                >
                                  {meta.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
  );
}

function AttendanceList({ orderedPositions, grouped, attendance, setStatus }) {
  return (
    <div className="space-y-4">
      {orderedPositions.map((pos) => (
        <div key={pos} data-testid={`attendance-pos-${pos}`}>
          <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-amber mb-2">
            {pos} <span className="text-slate-500">— {grouped[pos].length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {grouped[pos].map((pl) => <AttendanceRow key={pl.id} pl={pl} attendance={attendance} setStatus={setStatus} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function useAttendanceActions(practiceId, saved, onAttendanceChange) {
  const [busy, setBusy] = useState(false);
  const notify = (data) => onAttendanceChange && onAttendanceChange(data);
  const setStatus = async (playerId, currentStatus, newStatus) => {
    if (!saved) {
      toast.error("Save the practice first before taking attendance");
      return;
    }
    // Click same button toggles back to unmarked
    const finalStatus = currentStatus === newStatus ? "unmarked" : newStatus;
    try {
      const { data } = await api.patch(`/practices/${practiceId}/attendance`, {
        player_id: playerId, status: finalStatus,
      });
      notify(data);
    } catch (e) {
      console.error("Attendance update failed", e);
      toast.error("Could not update attendance");
    }
  };

  const markAllPresent = async () => {
    if (!saved) {
      toast.error("Save the practice first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/practices/${practiceId}/attendance/mark-all-present`);
      notify(data);
      toast.success("All rostered players marked present");
    } catch (e) {
      console.error("Attendance update failed", e);
      toast.error("Bulk mark failed");
    } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!saved) return;
    if (!window.confirm("Clear all attendance marks for this practice?")) return;
    setBusy(true);
    try {
      await api.delete(`/practices/${practiceId}/attendance`);
      notify({});
    } catch (e) {
      console.error("Attendance update failed", e);
      toast.error("Clear failed");
    } finally { setBusy(false); }
  };
  return { busy, setStatus, markAllPresent, clearAll };
}

export function AttendanceSection({ practiceId, saved, attendance, onAttendanceChange }) {
  const [expanded, setExpanded] = useState(false);
  const { roster, players, grouped, orderedPositions, counts } = useRosterGroups(attendance);
  const { setStatus, markAllPresent, clearAll } = useAttendanceActions(practiceId, saved, onAttendanceChange);
  const notReady = !saved || !roster || roster.players.length === 0;

  return (
    <div className="border-t border-surface-3" data-testid="attendance-section">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-6 py-4 hover:bg-surface-2 text-left"
        data-testid="attendance-toggle"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-amber" /> : <ChevronRight className="w-4 h-4 text-amber" />}
        <UserCheck className="w-4 h-4 text-amber" />
        <div className="flex-1">
          <div className="font-condensed uppercase tracking-widest text-sm text-white">Attendance</div>
          <AttendanceSummary saved={saved} roster={roster} counts={counts} players={players} />
        </div>
        {expanded && !notReady && (
          <>
            <div
              onClick={(e) => { e.stopPropagation(); markAllPresent(); }}
              data-testid="mark-all-present"
              className="cursor-pointer rounded-none bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 h-8 px-3 text-[10px] font-condensed uppercase tracking-widest flex items-center gap-1"
            >
              <CheckCircle2 className="w-3 h-3" /> Mark All Present
            </div>
            <div
              onClick={(e) => { e.stopPropagation(); clearAll(); }}
              data-testid="clear-attendance"
              className="cursor-pointer rounded-none bg-transparent hover:bg-surface-2 text-slate-400 border border-surface-3 hover:border-slate-500 h-8 px-3 text-[10px] font-condensed uppercase tracking-widest"
            >
              Clear
            </div>
          </>
        )}
      </button>

      {/* Body */}
      {expanded && (
        <div className="px-6 pb-6" data-testid="attendance-body">
          {notReady ? (
            <div className="text-sm text-slate-500 py-4">
              {!saved
                ? "Save the practice first — attendance is stored per practice."
                : !roster
                  ? "Loading roster…"
                  : "Head to Team Roster and sync from your Google Sheet."}
            </div>
          ) : (
            <AttendanceList orderedPositions={orderedPositions} grouped={grouped}
              attendance={attendance} setStatus={setStatus} />
          )}
        </div>
      )}
    </div>
  );
}
